/**
 * Organization API client.
 *
 * Mirrors workers/src/routes/organizations.js. Every call throws on a non-2xx
 * so callers get a real error instead of a silently empty result — the pattern
 * the rest of the services use.
 */
const API_URL = process.env.REACT_APP_API_URL || '';

const getHeaders = () => {
  const token = localStorage.getItem('sessionToken') || localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

async function call(method, path, body) {
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers: getHeaders(),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    throw new Error(data.error || `Request failed (${res.status})`);
  }
  return data;
}

export const listOrganizations = () => call('GET', '/organizations');
export const createOrganization = (name, slug) =>
  call('POST', '/organizations', { name, slug });
export const getOrganization = (id) => call('GET', `/organizations/${id}`);

export const listMembers = (id) => call('GET', `/organizations/${id}/members`);
export const setMemberRole = (id, userId, role) =>
  call('PATCH', `/organizations/${id}/members/${userId}`, { role });
export const removeMember = (id, userId) =>
  call('DELETE', `/organizations/${id}/members/${userId}`);

export const listInvitations = (id) => call('GET', `/organizations/${id}/invitations`);
export const inviteMember = (id, email, role) =>
  call('POST', `/organizations/${id}/invitations`, { email, role });
export const revokeInvitation = (id, invitationId) =>
  call('DELETE', `/organizations/${id}/invitations/${invitationId}`);
export const acceptInvitation = (token) =>
  call('POST', '/organizations/invitations/accept', { token });

export const getBilling = (id) => call('GET', `/organizations/${id}/billing`);
export const setSpendingLimit = (id, limitCents, period) =>
  call('PUT', `/organizations/${id}/billing/limit`, { limit_cents: limitCents, period });
export const grantCredit = (id, amountCents, description) =>
  call('POST', `/organizations/${id}/billing/grant`, { amount_cents: amountCents, description });

/**
 * The org the user is currently acting as, or null for personal.
 *
 * Kept in localStorage rather than the session so switching context never
 * needs a round trip, and so a stale server-side "current org" can't outlive
 * membership being revoked — the backend re-checks membership on every call
 * regardless of what this says.
 */
const ACTIVE_ORG_KEY = 'tcf.activeOrganizationId';

export const getActiveOrganizationId = () => {
  const raw = localStorage.getItem(ACTIVE_ORG_KEY);
  return raw ? Number(raw) : null;
};

export const setActiveOrganizationId = (id) => {
  if (id == null) localStorage.removeItem(ACTIVE_ORG_KEY);
  else localStorage.setItem(ACTIVE_ORG_KEY, String(id));
  window.dispatchEvent(new CustomEvent('tcf:active-org-changed', { detail: { id } }));
};

export default {
  listOrganizations, createOrganization, getOrganization,
  listMembers, setMemberRole, removeMember,
  listInvitations, inviteMember, revokeInvitation, acceptInvitation,
  getBilling, setSpendingLimit, grantCredit,
  getActiveOrganizationId, setActiveOrganizationId,
};
